/**
 * /legal/* — páginas legales públicas (Ley 1581 / 2012, Habeas Data Colombia).
 *
 * Estructura inspirada en Lexflow (proyecto hermano del mismo dueño con
 * documentos legales de mejor calidad y diagramado más profesional).
 * Adaptado a Taxor: facturación electrónica DIAN, datos de Sociedad
 * Colombiana de Consultoría, fuentes externas (DIAN, RUES) en vez de
 * SECOP / Colombia Compra Eficiente.
 *
 * Cuatro documentos:
 *   /legal/terminos        Términos y Condiciones de Uso
 *   /legal/privacidad      Política de Tratamiento de Datos Personales
 *   /legal/habeas-data     Autorización Expresa Habeas Data (Ley 1581)
 *   /legal/cookies         Política de Cookies
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Cookie,
  FileCheck,
  FileText,
  Lock,
  ScrollText,
  Shield,
  ShieldCheck,
  Sparkles,
  UserCheck,
} from 'lucide-react'

import { ThemeToggle } from '@/components/ui/theme-toggle'
import { APP, ROUTES } from '@/lib/constants'

// ─── Datos del responsable (visibles en los 4 docs) ────────────────

const RESPONSABLE = {
  name: 'Sociedad Colombiana de Consultoría',
  nit: '900.380.987-8',
  address: 'Bogotá D.C., Colombia',
  email: 'contacto@taxor.co',
website: 'https://taxor.co',
  app_url: 'https://admin.taxor.co',
}

const LAST_UPDATED = '2 de mayo de 2026'

// ─── Layout primitives ────────────────────────────────────────────

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-bg-app">
      <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-bg-surface/95 px-6 backdrop-blur">
        <a
          href={import.meta.env.VITE_APP_URL ?? 'https://taxor.co'}
          className="flex items-center gap-2"
        >
          <div
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-brand-500 to-brand-600 font-mono text-sm font-bold text-white shadow-sm"
          >
            Tx
          </div>
          <span className="text-base font-semibold text-fg-primary">{APP.name}</span>
        </a>
        <ThemeToggle />
      </header>
      <main className="mx-auto max-w-4xl px-4 py-12">{children}</main>
    </div>
  )
}

interface PageHeaderProps {
  icon: ReactNode
  title: string
  subtitle?: string
}

function PageHeader({ icon, title, subtitle }: PageHeaderProps) {
  return (
    <div className="mb-8">
      <Link
        to={ROUTES.LOGIN}
        className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-secondary"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Volver al login
      </Link>

      <div className="mt-4 flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-500/10 text-brand-600 ring-1 ring-brand-500/20">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-fg-primary">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm font-medium text-brand-600">{subtitle}</p>
          )}
          <p className="mt-2 text-sm text-fg-muted">
            Última actualización:{' '}
            <strong className="text-fg-secondary">{LAST_UPDATED}</strong>
          </p>
        </div>
      </div>
    </div>
  )
}

interface SectionProps {
  number?: string
  title: string
  icon?: ReactNode
  variant?: 'default' | 'danger' | 'success' | 'warning' | 'brand'
  children: ReactNode
}

function Section({ number, title, icon, variant = 'default', children }: SectionProps) {
  const variantClasses: Record<string, string> = {
    default: 'border-border bg-bg-surface',
    danger: 'border-error/30 bg-error-tint/40',
    success: 'border-success-fg/30 bg-success-tint/40',
    warning: 'border-warning-fg/30 bg-warning-tint/40',
    brand: 'border-brand-500/30 bg-brand-500/5',
  }
  const titleClasses: Record<string, string> = {
    default: 'text-fg-primary',
    danger: 'text-error-fg',
    success: 'text-success-fg',
    warning: 'text-warning-fg',
    brand: 'text-brand-600',
  }
  return (
    <section className={`rounded-2xl border-2 p-6 shadow-sm ${variantClasses[variant]}`}>
      <h2
        className={`flex items-center gap-2 text-lg font-bold ${titleClasses[variant]}`}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        {number && <span className="font-mono text-fg-muted">{number}.</span>}
        {title}
      </h2>
      <div className="mt-4 space-y-3 text-sm leading-relaxed text-fg-secondary [&_a]:font-medium [&_a]:text-brand-600 [&_a:hover]:underline [&_strong]:text-fg-primary [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5 [&_li]:marker:text-fg-muted">
        {children}
      </div>
    </section>
  )
}

function RelatedDocs({ except }: { except: 'terms' | 'privacy' | 'habeas-data' | 'cookies' }) {
  const all: { kind: typeof except; label: string; to: string }[] = [
    { kind: 'terms', label: 'Términos y Condiciones', to: '/legal/terminos' },
    { kind: 'privacy', label: 'Política de Privacidad', to: '/legal/privacidad' },
    { kind: 'habeas-data', label: 'Autorización Habeas Data', to: '/legal/habeas-data' },
    { kind: 'cookies', label: 'Política de Cookies', to: '/legal/cookies' },
  ]
  const others = all.filter((d) => d.kind !== except)
  return (
    <Section title="Documentos relacionados" variant="brand" icon={<Sparkles className="h-5 w-5" />}>
      <div className="grid gap-2 sm:grid-cols-2">
        {others.map((d) => (
          <Link
            key={d.kind}
            to={d.to}
            className="rounded-lg border border-border bg-bg-surface px-4 py-3 text-sm font-medium text-fg-primary transition-colors hover:border-brand-500/40 hover:bg-bg-elevated"
          >
            {d.label} →
          </Link>
        ))}
      </div>
      <div className="mt-4 border-t border-border pt-4 text-xs text-fg-muted">
        Para reclamaciones de habeas data o ejercicio de derechos del titular escribe a{' '}
        <strong className="text-fg-secondary">{RESPONSABLE.email}</strong>. La autoridad
        de control en Colombia es la SIC (Superintendencia de Industria y Comercio):{' '}
        <a
          href="https://www.sic.gov.co"
          target="_blank"
          rel="noreferrer"
          className="text-brand-600 hover:underline"
        >
          sic.gov.co
        </a>
        .
      </div>
    </Section>
  )
}

function ResponsableInfo() {
  return (
    <div className="rounded-lg border border-border bg-bg-app/40 p-4 text-sm text-fg-secondary">
      <ul className="space-y-1.5">
        <li><strong>Razón social:</strong> {RESPONSABLE.name}</li>
        <li><strong>NIT:</strong> {RESPONSABLE.nit}</li>
        <li><strong>Domicilio:</strong> {RESPONSABLE.address}</li>
        <li>
          <strong>Correo de contacto (incluido habeas data):</strong>{' '}
          <a href={`mailto:${RESPONSABLE.email}`}>{RESPONSABLE.email}</a>
        </li>
        <li>
          <strong>Sitio web:</strong>{' '}
          <a href={RESPONSABLE.website} target="_blank" rel="noreferrer">
            {RESPONSABLE.website}
          </a>
        </li>
      </ul>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Página 1 — TÉRMINOS Y CONDICIONES
// ════════════════════════════════════════════════════════════════════

export function TermsPage() {
  return (
    <Shell>
      <PageHeader
        icon={<ScrollText className="h-6 w-6" />}
        title="Términos y Condiciones de Uso"
      />

      <div className="space-y-6">
        <Section title="Introducción" variant="brand" icon={<FileText className="h-5 w-5" />}>
          <p>
            Los presentes Términos y Condiciones (en adelante, los "Términos") regulan el
            acceso y uso de la plataforma <strong>TAXOR</strong> (en adelante, la
            "Plataforma"), operada por <strong>{RESPONSABLE.name}</strong>, sociedad
            constituida bajo las leyes de la República de Colombia, identificada con NIT{' '}
            <strong>{RESPONSABLE.nit}</strong>, con domicilio en{' '}
            <strong>{RESPONSABLE.address}</strong> (en adelante, "TAXOR", "nosotros" o la
            "Empresa").
          </p>
          <p>
            Al acceder, registrarse o utilizar la Plataforma, el usuario (en adelante, el
            "Usuario" o "usted") declara haber leído, comprendido y aceptado íntegramente
            estos Términos, así como nuestra{' '}
            <Link to="/legal/privacidad">Política de Privacidad</Link> y la{' '}
            <Link to="/legal/habeas-data">Autorización Expresa Habeas Data</Link>.
          </p>
        </Section>

        <Section
          title="Si no está de acuerdo con estos Términos, absténgase de utilizar la Plataforma."
          variant="warning"
          icon={<AlertTriangle className="h-5 w-5" />}
        >
          <p>
            Su consentimiento es expreso y queda registrado con fecha, hora, dirección IP y
            versión exacta del documento aceptado. Conserve este documento.
          </p>
        </Section>

        <Section number="1" title="Definiciones" icon={<FileText className="h-5 w-5" />}>
          <ul>
            <li>
              <strong>Plataforma:</strong> el software como servicio (SaaS) TAXOR,
              accesible a través de <code>https://taxor.co</code> y sus subdominios
              (incluido <code>{RESPONSABLE.app_url}</code>).
            </li>
            <li>
              <strong>Servicios:</strong> las funcionalidades de emisión, recepción,
              custodia y administración de documentos electrónicos tributarios (factura
              electrónica de venta, notas crédito, notas débito, documentos soporte y
              demás documentos electrónicos autorizados por la DIAN), conforme a la
              Resolución 042 de 2020 de la DIAN y normas concordantes.
            </li>
            <li>
              <strong>Usuario:</strong> toda persona natural o jurídica que acceda, se
              registre o utilice la Plataforma.
            </li>
            <li>
              <strong>Cuenta:</strong> el registro único del Usuario que le permite
              acceder a los Servicios.
            </li>
            <li>
              <strong>Contenido de Terceros:</strong> información proveniente de fuentes
              externas como la DIAN, el Registro Único Empresarial y Social (RUES) de las
              Cámaras de Comercio, y otras bases de datos públicas o privadas que la
              Plataforma consulta para validar identificación, vigencia y datos
              tributarios.
            </li>
            <li>
              <strong>Suscripción:</strong> el plan de pago seleccionado por el Usuario
              para acceder a determinadas funcionalidades.
            </li>
          </ul>
        </Section>

        <Section
          number="2"
          title="Naturaleza del servicio"
          icon={<ShieldCheck className="h-5 w-5" />}
        >
          <p>
            <strong>2.1.</strong> TAXOR es un proveedor tecnológico autorizado por la
            DIAN para la emisión y recepción de documentos electrónicos. La Plataforma
            facilita al Usuario el cumplimiento de sus obligaciones formales en materia
            de facturación electrónica.
          </p>
          <p>
            <strong>2.2.</strong> La Plataforma <strong>NO CONSTITUYE</strong> un servicio
            de asesoría legal, financiera, contable ni tributaria. Las funcionalidades
            de validación, generación y reportes son meramente operacionales y no deben
            interpretarse como consejo profesional. La responsabilidad como contribuyente
            es <strong>indelegable</strong>.
          </p>
          <p>
            <strong>2.3.</strong> La Plataforma actúa como{' '}
            <strong>intermediario tecnológico</strong> que conecta al Usuario con los
            sistemas de la DIAN y consulta información en RUES y otras fuentes externas.
            TAXOR no es el origen ni custodio único de dicha información.
          </p>
        </Section>

        {/* Cláusula esencial — exoneración */}
        <Section
          number="3"
          title="Exoneración de responsabilidad — cláusula esencial"
          variant="danger"
          icon={<AlertTriangle className="h-5 w-5" />}
        >
          <p className="rounded-md border border-error/40 bg-error-tint p-3 font-medium uppercase text-error-fg">
            EL USUARIO RECONOCE, ACEPTA Y RENUNCIA EXPRESAMENTE A INICIAR CUALQUIER TIPO
            DE ACCIÓN JUDICIAL, EXTRAJUDICIAL, ADMINISTRATIVA O DE CUALQUIER OTRA
            NATURALEZA CONTRA:
          </p>
          <ul>
            <li>{RESPONSABLE.name}</li>
            <li>TAXOR y sus marcas relacionadas</li>
            <li>Cualquier empresa matriz, filial, subsidiaria, subordinada o relacionada</li>
            <li>Sus accionistas, directores, empleados, contratistas y representantes</li>
          </ul>

          <p className="mt-4">
            <strong>3.1. Exoneración por fallas técnicas.</strong> TAXOR no será
            responsable por daños, perjuicios, lucro cesante o cualquier afectación
            derivada de:
          </p>
          <ul>
            <li>Fallas, errores o interrupciones en el funcionamiento de la Plataforma.</li>
            <li>Retardos, demoras o lentitud en la prestación del Servicio.</li>
            <li>Caídas de red, interrupciones de conectividad o problemas de infraestructura.</li>
            <li>Pérdida parcial o total de datos almacenados en la Plataforma.</li>
            <li>Dificultades de conexión, acceso o autenticación.</li>
            <li>Ataques informáticos, intentos de hackeo o vulneraciones de seguridad.</li>
            <li>Errores de software, bugs o comportamientos inesperados.</li>
            <li>Incompatibilidades con navegadores, dispositivos o sistemas operativos.</li>
            <li>Mantenimientos programados o de emergencia.</li>
            <li>
              Indisponibilidad o caídas de la DIAN, sus sistemas (CUFE, validación,
              recepción del XML) o de RUES.
            </li>
          </ul>

          <p className="mt-4">
            <strong>3.2. Exoneración por información de terceros.</strong> La Plataforma
            integra, consolida y presenta información proveniente de fuentes externas
            incluyendo, pero no limitándose a: DIAN, RUES, Cámaras de Comercio, y otras
            bases de datos públicas y privadas.
          </p>
          <p>
            <strong>
              TAXOR NO SE HACE RESPONSABLE, BAJO NINGUNA CIRCUNSTANCIA, POR:
            </strong>
          </p>
          <ul>
            <li>La exactitud, veracidad, completitud o actualidad de la información de terceros.</li>
            <li>Errores, omisiones o inexactitudes en los datos de DIAN, RUES u otras fuentes.</li>
            <li>Información desactualizada, incompleta o que no refleje la realidad actual.</li>
            <li>
              Decisiones comerciales, tributarias o de cualquier índole tomadas con base
              en dicha información.
            </li>
            <li>Pérdidas económicas derivadas de confiar en información que resulte incorrecta.</li>
            <li>La disponibilidad o continuidad de las fuentes de información externas.</li>
          </ul>

          <p className="mt-4">
            <strong>3.3. Obligación de verificación del Usuario.</strong> Es responsabilidad
            <strong> exclusiva</strong> del Usuario verificar, validar y confirmar toda la
            información obtenida a través de la Plataforma directamente con las fuentes
            oficiales correspondientes (DIAN, RUES, Cámaras de Comercio) antes de tomar
            cualquier decisión o emprender cualquier acción.
          </p>
          <p>
            El Usuario entiende y acepta que TAXOR es una herramienta de apoyo y
            facilitación operacional, no un sustituto de la debida diligencia profesional
            (contable, tributaria o legal) que corresponde realizar al Usuario o a sus
            asesores.
          </p>

          <p className="mt-4">
            <strong>3.4. Limitación de responsabilidad.</strong> En cualquier caso, y en
            la máxima medida permitida por la ley colombiana, la responsabilidad total
            de TAXOR por cualquier concepto no excederá el monto efectivamente pagado por
            el Usuario en los últimos doce (12) meses por concepto de Suscripción.
          </p>
        </Section>

        <Section
          number="4"
          title="Registro y cuenta de Usuario"
          icon={<UserCheck className="h-5 w-5" />}
        >
          <p>
            <strong>4.1.</strong> Para acceder a los Servicios, el Usuario debe crear una
            Cuenta proporcionando información veraz, completa y actualizada, incluyendo
            nombres, NIT (cuando aplique) y correo electrónico válido. El Usuario se
            compromete a mantener dicha información actualizada.
          </p>
          <p>
            <strong>4.2.</strong> El Usuario es responsable de mantener la confidencialidad
            de sus credenciales de acceso (contraseña, códigos TOTP de 2FA, códigos de
            respaldo) y de todas las actividades realizadas bajo su Cuenta.
          </p>
          <p>
            <strong>4.3.</strong> El Usuario debe notificar inmediatamente a TAXOR
            cualquier uso no autorizado de su Cuenta o cualquier otra violación de
            seguridad escribiendo a <strong>{RESPONSABLE.email}</strong>.
          </p>
          <p>
            <strong>4.4.</strong> TAXOR se reserva el derecho de suspender o cancelar
            Cuentas que violen estos Términos o que presenten actividad sospechosa
            (intentos masivos de login, uso irregular del API, emisión de documentos con
            datos manifiestamente falsos, etc).
          </p>
        </Section>

        <Section number="5" title="Suscripciones y pagos">
          <p>
            <strong>5.1.</strong> La Plataforma ofrece planes de Suscripción con diferentes
            niveles de funcionalidad. Los precios y características de cada plan están
            disponibles en <a href="https://taxor.co/precios" target="_blank" rel="noreferrer">taxor.co/precios</a>.
          </p>
          <p>
            <strong>5.2.</strong> Las Suscripciones se renuevan automáticamente al
            finalizar cada período de facturación, salvo que el Usuario las cancele
            previamente desde Mi cuenta.
          </p>
          <p>
            <strong>5.3.</strong> Los pagos realizados no son reembolsables, excepto en
            los casos expresamente previstos por la Ley 1480 de 2011 (Estatuto del
            Consumidor).
          </p>
          <p>
            <strong>5.4.</strong> TAXOR se reserva el derecho de modificar los precios de
            las Suscripciones con un preaviso de treinta (30) días.
          </p>
          <p>
            <strong>5.5.</strong> El incumplimiento en el pago superior a treinta (30)
            días faculta a TAXOR para suspender o cancelar el acceso a los Servicios sin
            responsabilidad. Los documentos electrónicos ya emitidos permanecen
            disponibles conforme a la retención DIAN (5 años).
          </p>
        </Section>

        <Section number="6" title="Uso aceptable de la Plataforma">
          <p>El Usuario se compromete a utilizar la Plataforma de manera lícita y conforme a estos Términos. Queda expresamente prohibido:</p>
          <ul>
            <li>Utilizar la Plataforma para emitir documentos falsos, fraudulentos o no respaldados por operaciones reales.</li>
            <li>Intentar acceder a sistemas, datos o funcionalidades no autorizadas (incluido el acceso a documentos de otros tax_clients).</li>
            <li>Realizar ingeniería inversa, descompilar o intentar extraer el código fuente.</li>
            <li>Compartir credenciales de acceso con terceros no autorizados.</li>
            <li>Automatizar el acceso mediante bots, scrapers o herramientas similares sin autorización (las integraciones programáticas requieren API keys emitidas por TAXOR).</li>
            <li>Sobrecargar, dañar o interferir con la infraestructura de la Plataforma.</li>
            <li>Revender, sublicenciar o comercializar los Servicios sin autorización escrita.</li>
            <li>Utilizar la información obtenida para competir directamente con TAXOR.</li>
          </ul>
        </Section>

        <Section number="7" title="Propiedad intelectual">
          <p>
            <strong>7.1.</strong> Todos los derechos de propiedad intelectual sobre la
            Plataforma, incluyendo software, diseño, marcas, logotipos, interfaces y
            contenido original, pertenecen exclusivamente a {RESPONSABLE.name}.
          </p>
          <p>
            <strong>7.2.</strong> El acceso a la Plataforma no otorga al Usuario ningún
            derecho de propiedad intelectual sobre la misma, más allá de una licencia
            limitada, no exclusiva y revocable para su uso personal o empresarial conforme
            a la Suscripción contratada.
          </p>
          <p>
            <strong>7.3.</strong> El Usuario retiene los derechos sobre los documentos
            electrónicos y la información que ingrese a la Plataforma. Otorga a TAXOR una
            licencia para procesarlos con el único fin de prestar los Servicios y cumplir
            las obligaciones tributarias delegadas.
          </p>
        </Section>

        <Section number="8" title="Terminación">
          <p>
            <strong>8.1.</strong> El Usuario puede eliminar su Cuenta en cualquier momento
            desde <strong>Mi cuenta → Zona de peligro</strong>. La eliminación es soft con
            un periodo de gracia de 30 días, pasado el cual los datos se eliminan
            permanentemente conforme a la{' '}
            <Link to="/legal/privacidad">Política de Privacidad</Link>.
          </p>
          <p>
            <strong>8.2.</strong> TAXOR puede suspender o terminar el acceso del Usuario
            por violación de estos Términos, actividad fraudulenta, incumplimiento de
            pago, o por cualquier causa legal justificada.
          </p>
          <p>
            <strong>8.3.</strong> Tras la terminación, el Usuario perderá acceso a su
            Cuenta y a todos los datos almacenados, salvo los que la ley exige conservar
            (registros tributarios DIAN: 5 años).
          </p>
          <p>
            <strong>8.4.</strong> Las cláusulas de exoneración de responsabilidad,
            propiedad intelectual y ley aplicable sobrevivirán a la terminación.
          </p>
        </Section>

        <Section number="9" title="Modificaciones a los Términos">
          <p>
            <strong>9.1.</strong> TAXOR se reserva el derecho de modificar estos Términos
            en cualquier momento. Las modificaciones serán notificadas al Usuario mediante
            correo electrónico o aviso en la Plataforma con al menos quince (15) días de
            anticipación.
          </p>
          <p>
            <strong>9.2.</strong> Si la modificación amplía propósitos del tratamiento de
            datos o introduce nuevas obligaciones materiales, te pediremos
            <strong> re-aceptación expresa</strong> antes de seguir usando el servicio.
          </p>
          <p>
            <strong>9.3.</strong> Si el Usuario no está de acuerdo con las modificaciones,
            debe dejar de utilizar la Plataforma y eliminar su Cuenta.
          </p>
        </Section>

        <Section number="10" title="Ley aplicable y resolución de conflictos">
          <p>
            <strong>10.1.</strong> Estos Términos se rigen por las leyes de la República
            de Colombia.
          </p>
          <p>
            <strong>10.2.</strong> Para cualquier controversia derivada de estos Términos,
            las partes acuerdan someterse a la jurisdicción de los jueces y tribunales de
            la ciudad de <strong>Bogotá D.C., Colombia</strong>, renunciando expresamente
            a cualquier otro fuero que pudiera corresponderles.
          </p>
          <p>
            <strong>10.3.</strong> Antes de acudir a la vía judicial, las partes se
            comprometen a intentar resolver cualquier disputa de manera amistosa mediante
            negociación directa durante un período mínimo de treinta (30) días.
          </p>
        </Section>

        <Section number="11" title="Contacto">
          <ResponsableInfo />
        </Section>

        <Section
          title="Aceptación íntegra"
          variant="brand"
          icon={<CheckCircle2 className="h-5 w-5" />}
        >
          <p>
            Al utilizar TAXOR, usted reconoce haber leído, comprendido y aceptado
            íntegramente estos Términos y Condiciones de Uso, incluyendo las cláusulas de
            exoneración de responsabilidad de la sección 3.
          </p>
        </Section>

        <RelatedDocs except="terms" />
      </div>
    </Shell>
  )
}

// ════════════════════════════════════════════════════════════════════
// Página 2 — POLÍTICA DE PRIVACIDAD
// ════════════════════════════════════════════════════════════════════

export function PrivacyPage() {
  return (
    <Shell>
      <PageHeader
        icon={<Shield className="h-6 w-6" />}
        title="Política de Tratamiento de Datos Personales"
        subtitle="Conforme a la Ley 1581 de 2012 y el Decreto 1377 de 2013"
      />

      <div className="space-y-6">
        <Section title="Responsable del Tratamiento" variant="brand" icon={<UserCheck className="h-5 w-5" />}>
          <p>
            <strong>{RESPONSABLE.name}</strong>, identificada con NIT{' '}
            <strong>{RESPONSABLE.nit}</strong>, con domicilio en{' '}
            <strong>{RESPONSABLE.address}</strong>, en calidad de Responsable del
            Tratamiento de datos personales, presenta la siguiente Política conforme a lo
            establecido en la Ley 1581 de 2012, el Decreto 1377 de 2013 y demás normas
            concordantes de la República de Colombia.
          </p>
          <p>
            Esta política describe cómo recopilamos, usamos, almacenamos, protegemos y
            compartimos la información personal de los usuarios de la plataforma{' '}
            <strong>TAXOR</strong>.
          </p>
          <ResponsableInfo />
        </Section>

        <Section
          number="1"
          title="Definiciones (art. 3, Ley 1581 de 2012)"
          icon={<FileText className="h-5 w-5" />}
        >
          <ul>
            <li><strong>Autorización:</strong> consentimiento previo, expreso e informado del Titular para llevar a cabo el Tratamiento de datos personales.</li>
            <li><strong>Base de Datos:</strong> conjunto organizado de datos personales que sea objeto de Tratamiento.</li>
            <li><strong>Dato Personal:</strong> cualquier información vinculada o que pueda asociarse a una o varias personas naturales determinadas o determinables.</li>
            <li><strong>Dato Público:</strong> dato que no sea semiprivado, privado o sensible. Son públicos, entre otros, los datos relativos al estado civil, profesión u oficio.</li>
            <li><strong>Dato Sensible:</strong> datos que afectan la intimidad del Titular o cuyo uso indebido puede generar discriminación (origen racial, orientación política, convicciones religiosas, datos de salud o vida sexual, datos biométricos).</li>
            <li><strong>Encargado del Tratamiento:</strong> persona natural o jurídica que realice el Tratamiento de datos personales por cuenta del Responsable.</li>
            <li><strong>Responsable del Tratamiento:</strong> persona natural o jurídica que decide sobre la base de datos y/o el Tratamiento de los datos.</li>
            <li><strong>Titular:</strong> persona natural cuyos datos personales sean objeto de Tratamiento.</li>
            <li><strong>Tratamiento:</strong> cualquier operación sobre datos personales: recolección, almacenamiento, uso, circulación o supresión.</li>
          </ul>
        </Section>

        <Section number="2" title="Datos personales que recopilamos">
          <p><strong>2.1. Datos de identificación y contacto:</strong></p>
          <ul>
            <li>Nombres y apellidos completos</li>
            <li>Correo electrónico</li>
            <li>Número de teléfono (con código país)</li>
            <li>País de residencia</li>
            <li>Cargo o posición en la empresa (cuando aplique)</li>
          </ul>

          <p className="mt-3"><strong>2.2. Datos de la empresa (cuando el registro es empresarial):</strong></p>
          <ul>
            <li>NIT y dígito de verificación</li>
            <li>Razón social y nombre comercial</li>
            <li>Tipo de organización jurídica</li>
            <li>Actividades económicas (códigos CIIU)</li>
            <li>Dirección, departamento y municipio</li>
            <li>Datos consultados en RUES vía API pública del Estado colombiano</li>
          </ul>

          <p className="mt-3"><strong>2.3. Datos de uso de la Plataforma:</strong></p>
          <ul>
            <li>Dirección IP y geolocalización aproximada</li>
            <li>Tipo de navegador, sistema operativo y dispositivo (User-Agent)</li>
            <li>Sesiones activas (fecha de creación, última actividad, fingerprint)</li>
            <li>Audit log: cada login exitoso o fallido, cambio de contraseña, activación/desactivación de 2FA, etc.</li>
            <li>Preferencias y configuraciones (idioma, zona horaria, tema visual)</li>
          </ul>

          <p className="mt-3"><strong>2.4. Datos de documentos electrónicos:</strong></p>
          <ul>
            <li>Información tributaria que tú emites a través de la Plataforma (factura, notas crédito/débito, documentos soporte) conforme exige la DIAN.</li>
            <li>Contenido del XML del DIAN, CUFE/CUDE, fechas de emisión y aceptación.</li>
            <li>Datos de los receptores de tus documentos cuando los registras.</li>
          </ul>

          <p className="mt-3"><strong>2.5. Datos de transacciones:</strong></p>
          <ul>
            <li>Historial de suscripciones</li>
            <li>Métodos de pago (procesados por terceros autorizados — TAXOR no almacena datos sensibles de tarjetas)</li>
            <li>Facturas y comprobantes</li>
          </ul>

          <p className="mt-3"><strong>2.6. Datos sensibles:</strong> TAXOR <strong>NO recopila</strong> datos sensibles del Titular (origen racial, orientación política, convicciones religiosas, datos de salud, datos biométricos) ya que no son necesarios para los Servicios.</p>
        </Section>

        <Section number="3" title="Finalidades del tratamiento">
          <p><strong>3.1. Finalidades principales:</strong></p>
          <ul>
            <li>Crear y gestionar la cuenta del Usuario en la Plataforma.</li>
            <li>Identificar y autenticar usuarios (login, MFA, sesiones).</li>
            <li>Emitir documentos electrónicos ante la DIAN en nombre del Usuario.</li>
            <li>Verificar la identidad del Usuario y la información de su empresa contra fuentes oficiales (RUES, DIAN).</li>
            <li>Procesar pagos y gestionar suscripciones.</li>
            <li>Comunicación operacional (avisos de servicio, alertas de seguridad como "nuevo dispositivo", confirmaciones de cambios sensibles).</li>
            <li>Brindar soporte técnico y atención al cliente.</li>
            <li>Cumplir obligaciones legales y tributarias (retención DIAN: 5 años).</li>
            <li>Detectar y prevenir fraude o abuso del Servicio.</li>
          </ul>

          <p className="mt-3"><strong>3.2. Finalidades secundarias (requieren autorización expresa adicional):</strong></p>
          <ul>
            <li>Enviar comunicaciones comerciales y promocionales de TAXOR.</li>
            <li>Realizar estudios de mercado y análisis estadísticos agregados y anonimizados para mejorar la Plataforma.</li>
            <li>Personalizar la experiencia del Usuario mediante análisis de comportamiento.</li>
          </ul>
        </Section>

        <Section
          number="4"
          title="Derechos del Titular de los datos"
          variant="brand"
          icon={<UserCheck className="h-5 w-5" />}
        >
          <p>Conforme al art. 8 de la Ley 1581 de 2012, el Titular tiene los siguientes derechos:</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              ['Conocer', 'Acceder de forma gratuita a los datos personales que hayan sido objeto de Tratamiento. En TAXOR: Mi cuenta → Privacidad → Descargar mis datos (JSON con todo).'],
              ['Actualizar', 'Solicitar la actualización de datos personales. En TAXOR: editable directamente desde Mi cuenta → Datos personales.'],
              ['Rectificar', 'Solicitar la corrección de información incorrecta, incompleta o inexacta.'],
              ['Suprimir', 'Solicitar la eliminación de los datos cuando no se respeten los principios, derechos y garantías legales. En TAXOR: Mi cuenta → Zona de peligro → Eliminar mi cuenta.'],
              ['Revocar', 'Revocar la autorización de tratamiento en cualquier momento.'],
              ['Solicitar prueba', 'Solicitar prueba de la autorización otorgada (te la generamos como JSON desde Mi cuenta → Privacidad).'],
              ['Informarse', 'Ser informado sobre el uso que se le ha dado a sus datos personales.'],
              ['Presentar quejas', 'Presentar quejas ante la SIC por infracciones a la ley.'],
            ].map(([t, d]) => (
              <div key={t} className="rounded-lg border border-border bg-bg-app/40 p-3">
                <p className="font-bold text-brand-600">{t}</p>
                <p className="mt-1 text-xs text-fg-secondary">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-4">
            Para ejercer estos derechos, escriba a <strong>{RESPONSABLE.email}</strong>{' '}
            con asunto <em>"Solicitud Habeas Data — [tipo de solicitud]"</em>.
          </p>
        </Section>

        <Section number="5" title="Medidas de seguridad" icon={<Lock className="h-5 w-5" />}>
          <p>
            {RESPONSABLE.name} implementa medidas técnicas, humanas y administrativas para
            proteger los datos personales contra acceso no autorizado, pérdida, alteración,
            destrucción o uso indebido. En particular:
          </p>
          <ul>
            <li><strong>Cifrado en tránsito:</strong> todas las comunicaciones usan TLS 1.3 con HSTS preload.</li>
            <li><strong>Cifrado en reposo:</strong> AES-256-GCM para datos sensibles (secretos TOTP, claves de firma DIAN, secretos de webhook).</li>
            <li><strong>Hashing de contraseñas:</strong> Argon2id (t=3, m=64MB, p=4) — TAXOR <strong>nunca</strong> conoce tu contraseña en texto plano.</li>
            <li><strong>JWT firmado con RS256</strong> y rotación automática de claves cada 90 días vía JWKS.</li>
            <li><strong>Autenticación de dos factores (TOTP RFC 6238)</strong> opcional con códigos de respaldo. Banking-grade: MFA se exige incluso en login OAuth si está activada.</li>
            <li><strong>Detección de patrones distribuidos</strong> (credential stuffing): bloqueo automático ante intentos desde múltiples IPs en ventanas cortas.</li>
            <li><strong>Notificación automática</strong> al titular cuando hay login desde un dispositivo nuevo.</li>
            <li><strong>Audit log append-only</strong> con 5 años de retención (cumple obligaciones DIAN).</li>
            <li><strong>Backups cifrados</strong> con GPG AES-256.</li>
            <li><strong>Headers de seguridad bancaria</strong> en cada respuesta HTTP (CSP, COR-P, Permissions-Policy, X-Frame-Options DENY).</li>
            <li><strong>Capacitación</strong> del personal en protección de datos personales y políticas internas documentadas.</li>
          </ul>
        </Section>

        <Section number="6" title="Transferencia y transmisión de datos">
          <p>
            <strong>6.1.</strong> Los datos personales pueden ser transmitidos a Encargados
            del Tratamiento (proveedores de servicios) que asisten en la operación de la
            Plataforma:
          </p>
          <ul>
            <li><strong>Hetzner Online GmbH</strong> (Alemania) — proveedor de infraestructura, con cláusulas de transferencia conforme al GDPR. Servidores físicos en Falkenstein y Helsinki.</li>
            <li><strong>DIAN</strong> (Colombia) — para la recepción y validación de documentos electrónicos. Esta transmisión es <strong>obligatoria</strong> para el servicio.</li>
            <li><strong>RUES (Cámaras de Comercio)</strong> — consulta de información empresarial pública, vía API.</li>
            <li><strong>Procesadores de pagos</strong> autorizados (no almacenamos datos sensibles de tarjetas).</li>
            <li><strong>Servicios de correo electrónico transaccional</strong> (SMTP) para enviar OTPs y notificaciones.</li>
            <li><strong>Google y Microsoft</strong> únicamente cuando el Usuario decide autenticarse vía OAuth con esos proveedores. Recibimos <em>solo</em> el correo y nombre verificados.</li>
          </ul>
          <p>
            <strong>6.2.</strong> Estos Encargados están obligados contractualmente a tratar
            los datos únicamente para las finalidades autorizadas, implementar medidas de
            seguridad adecuadas y eliminar los datos al finalizar la relación.
          </p>
          <p>
            <strong>6.3.</strong> En caso de transferencia internacional, se garantiza que
            el país receptor proporcione niveles adecuados de protección o se implementan
            las salvaguardas contractuales necesarias.
          </p>
        </Section>

        <Section number="7" title="Período de conservación">
          <ul>
            <li><strong>Datos de cuenta activa:</strong> durante la vigencia de la relación comercial.</li>
            <li><strong>Datos de cuenta eliminada:</strong> 30 días de gracia + borrado permanente.</li>
            <li><strong>Documentos electrónicos DIAN:</strong> 5 años (obligación legal art. 632 ETN).</li>
            <li><strong>Audit log:</strong> 5 años (obligación legal y forense).</li>
            <li><strong>Datos de facturación de TAXOR a clientes:</strong> 10 años (obligaciones tributarias).</li>
            <li><strong>Logs de seguridad:</strong> 2 años.</li>
            <li><strong>Comunicaciones de soporte:</strong> 3 años.</li>
          </ul>
          <p>
            Transcurridos estos plazos, los datos serán eliminados de forma segura o
            anonimizados.
          </p>
        </Section>

        <Section number="8" title="Uso de cookies y tecnologías similares">
          <p>
            Detalle completo en la <Link to="/legal/cookies">Política de Cookies</Link>.
            Resumen:
          </p>
          <ul>
            <li><strong>Cookies estrictamente necesarias:</strong> únicamente <code>taxor_refresh</code> (HttpOnly, Secure, SameSite=None, scope <code>/api/v1/auth/refresh</code>) para mantener tu sesión sin re-loguear cada 15 minutos.</li>
            <li><strong>Cookies analíticas / publicitarias:</strong> <strong>cero</strong>. Si en el futuro las agregamos, te pediremos consentimiento explícito.</li>
            <li><strong>localStorage:</strong> únicamente para preferencias de UI no sensibles (tema claro/oscuro).</li>
          </ul>
        </Section>

        <Section number="9" title="Datos de menores de edad">
          <p>
            La Plataforma está diseñada exclusivamente para personas naturales mayores de
            18 años o que tengan capacidad legal para contratar. <strong>No recopilamos
            intencionalmente datos de menores de edad</strong>. Si detectamos que hemos
            recopilado datos de un menor sin la autorización de su representante legal,
            procederemos a eliminarlos de inmediato.
          </p>
        </Section>

        <Section number="10" title="Modificaciones a la política">
          <p>
            Esta Política puede ser actualizada periódicamente. Los cambios significativos
            serán notificados mediante correo electrónico al titular o aviso en la
            Plataforma. La fecha de última actualización se indica al inicio de este
            documento. Si la modificación amplía propósitos del tratamiento, te pediremos
            <strong> autorización expresa adicional</strong>.
          </p>
        </Section>

        <Section number="11" title="Contacto y procedimiento de quejas">
          <p>Para ejercer sus derechos o presentar consultas o reclamos, contacte a:</p>
          <ResponsableInfo />
          <p className="mt-3"><strong>Procedimiento (art. 14 y 15 Ley 1581):</strong></p>
          <ul>
            <li>Las consultas serán atendidas en un término máximo de <strong>diez (10) días hábiles</strong>.</li>
            <li>Los reclamos serán atendidos en un término máximo de <strong>quince (15) días hábiles</strong>.</li>
            <li>Si no es posible atender en dicho plazo, se informará al Titular los motivos y la fecha de respuesta, que no excederá de <strong>ocho (8) días hábiles adicionales</strong>.</li>
          </ul>
          <p className="mt-3">
            <strong>Autoridad de control:</strong> Superintendencia de Industria y Comercio
            (SIC) —{' '}
            <a href="https://www.sic.gov.co" target="_blank" rel="noreferrer">
              www.sic.gov.co
            </a>
            .
          </p>
        </Section>

        <RelatedDocs except="privacy" />
      </div>
    </Shell>
  )
}

// ════════════════════════════════════════════════════════════════════
// Página 3 — AUTORIZACIÓN HABEAS DATA (autorización expresa)
// ════════════════════════════════════════════════════════════════════

export function HabeasDataPage() {
  return (
    <Shell>
      <PageHeader
        icon={<FileCheck className="h-6 w-6" />}
        title="Autorización para el Tratamiento de Datos Personales"
        subtitle="Ley 1581 de 2012 — Habeas Data"
      />

      <div className="space-y-6">
        <Section
          title="Autorización Expresa para el Tratamiento de Datos Personales"
          variant="brand"
          icon={<ShieldCheck className="h-5 w-5" />}
        >
          <p>
            Conforme a lo dispuesto en la <strong>Ley Estatutaria 1581 de 2012</strong>{' '}
            <em>"Por la cual se dictan disposiciones generales para la protección de datos
            personales"</em> y su <strong>Decreto Reglamentario 1377 de 2013</strong>.
          </p>
        </Section>

        <Section
          number="1"
          title="Responsable del tratamiento"
          icon={<UserCheck className="h-5 w-5" />}
        >
          <ResponsableInfo />
        </Section>

        <Section number="2" title="Declaración del Titular">
          <p>
            Mediante el registro en la plataforma TAXOR y la aceptación de esta
            autorización, el Titular de los datos personales manifiesta que:
          </p>
          <ul className="!list-none !pl-0 space-y-2">
            {[
              'Ha sido informado de manera previa, expresa y suficiente sobre las finalidades del tratamiento de sus datos personales.',
              'Ha tenido acceso a la Política de Tratamiento de Datos Personales de ' + RESPONSABLE.name + '.',
              'Conoce los derechos que le asisten como titular de los datos, especialmente los de conocer, actualizar, rectificar y suprimir la información, así como revocar la autorización.',
              'El suministro de la información es voluntario y no está obligado a proporcionar datos sensibles.',
              'La información proporcionada es veraz, completa, exacta, actualizada y comprobable.',
            ].map((s) => (
              <li key={s} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-fg" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          number="3"
          title="Autorización Expresa"
          variant="success"
          icon={<CheckCircle2 className="h-5 w-5" />}
        >
          <p>
            En virtud de lo anterior, <strong>AUTORIZO</strong> de manera libre, previa,
            expresa, voluntaria e informada a <strong>{RESPONSABLE.name}</strong> en
            calidad de Responsable del Tratamiento, y a quienes en el futuro actúen como
            Encargados del Tratamiento, para:
          </p>

          <p className="mt-3">
            <strong>a) Operaciones autorizadas:</strong> recolectar, almacenar, usar,
            circular, suprimir y en general tratar mis datos personales conforme a las
            finalidades descritas en la Política de Tratamiento de Datos Personales.
          </p>

          <p className="mt-3"><strong>b) Tipos de datos tratados:</strong></p>
          <ul>
            <li>Datos de identificación (nombres, apellidos, documento de identidad).</li>
            <li>Datos de contacto (correo electrónico, teléfono, dirección).</li>
            <li>Datos profesionales (cargo, empresa, perfil tributario).</li>
            <li>Datos de la empresa (NIT, razón social, información comercial pública obtenida vía RUES).</li>
            <li>Datos de uso de la plataforma (navegación, sesiones, preferencias, audit log).</li>
            <li>Datos de los documentos electrónicos que el Titular emita o reciba a través de la Plataforma.</li>
            <li>Datos de transacciones (suscripciones, pagos).</li>
          </ul>

          <p className="mt-3"><strong>c) Finalidades autorizadas:</strong></p>
          <ul>
            <li>Gestión de mi cuenta en la plataforma TAXOR.</li>
            <li>Prestación de los servicios de facturación electrónica DIAN (emisión, recepción, custodia, reportes).</li>
            <li>Verificación de identidad y validación de información empresarial contra fuentes oficiales (RUES, DIAN).</li>
            <li>Procesamiento de pagos y gestión de suscripciones.</li>
            <li>Envío de comunicaciones operacionales y de seguridad relacionadas con el servicio.</li>
            <li>Cumplimiento de obligaciones legales, tributarias y contractuales (incluida la retención DIAN de 5 años).</li>
            <li>Mejora continua de los servicios ofrecidos mediante análisis agregados y anonimizados.</li>
          </ul>

          <p className="mt-3">
            <strong>d) Transferencia y transmisión:</strong> transferir o transmitir los
            datos a terceros (Encargados del Tratamiento) que presten servicios necesarios
            para la operación de la Plataforma, siempre que garanticen niveles adecuados
            de protección. Lista detallada en la sección 6 de la{' '}
            <Link to="/legal/privacidad">Política de Privacidad</Link>.
          </p>
        </Section>

        <Section
          number="4"
          title="Autorizaciones adicionales (opcionales)"
          variant="warning"
          icon={<Sparkles className="h-5 w-5" />}
        >
          <p>
            Adicionalmente, el Titular puede otorgar las siguientes autorizaciones
            opcionales, las cuales pueden ser revocadas en cualquier momento desde{' '}
            <strong>Mi cuenta → Privacidad y datos</strong>:
          </p>
          <ol className="list-decimal space-y-3 pl-5">
            <li>
              <strong>Comunicaciones comerciales:</strong> autorizo el envío de
              comunicaciones comerciales, promocionales, ofertas especiales y contenido de
              marketing a través de correo electrónico u otros medios.
            </li>
            <li>
              <strong>Análisis y personalización:</strong> autorizo el análisis de mis
              patrones de uso para personalizar mi experiencia y recibir recomendaciones
              adaptadas a mis necesidades.
            </li>
            <li>
              <strong>Compartir con aliados:</strong> autorizo compartir información no
              sensible con aliados comerciales para recibir ofertas y servicios
              complementarios (cuando se anuncien explícitamente).
            </li>
          </ol>
        </Section>

        <Section
          number="5"
          title="Derechos del Titular"
          icon={<UserCheck className="h-5 w-5" />}
        >
          <p>Como Titular de los datos personales, tengo derecho a:</p>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              ['Conocer', 'Acceder a mis datos personales que hayan sido objeto de tratamiento.'],
              ['Actualizar', 'Solicitar la actualización de mis datos personales.'],
              ['Rectificar', 'Solicitar la corrección de información incorrecta, incompleta o inexacta.'],
              ['Suprimir', 'Solicitar la eliminación de mis datos cuando proceda legalmente.'],
              ['Revocar', 'Revocar esta autorización en cualquier momento.'],
              ['Reclamar', 'Presentar quejas ante la SIC por violación de mis derechos.'],
            ].map(([t, d]) => (
              <div
                key={t}
                className="rounded-lg border border-border bg-bg-app/40 p-3"
              >
                <p className="font-bold text-brand-600">{t}</p>
                <p className="mt-1 text-xs text-fg-secondary">{d}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section number="6" title="Canal de atención para el ejercicio de derechos">
          <p>Para ejercer cualquiera de los derechos mencionados, puede contactarnos a través de:</p>
          <ul>
            <li>
              <strong>Correo electrónico:</strong>{' '}
              <a href={`mailto:${RESPONSABLE.email}`}>
                {RESPONSABLE.email}
              </a>
            </li>
            <li>
              <strong>Asunto:</strong> "Solicitud Habeas Data — [tipo de solicitud]"
            </li>
          </ul>
          <p>
            Su solicitud será atendida dentro de los términos legales: <strong>diez (10)
            días hábiles</strong> para consultas y <strong>quince (15) días hábiles</strong>{' '}
            para reclamos.
          </p>
        </Section>

        <Section number="7" title="Vigencia de la autorización">
          <p>
            Esta autorización permanecerá vigente durante el tiempo en que el Titular
            mantenga una relación contractual con {RESPONSABLE.name} y, posteriormente,
            durante el período necesario para cumplir con las obligaciones legales
            aplicables (especialmente la retención de 5 años para registros DIAN).
          </p>
          <p>
            El Titular podrá revocar esta autorización en cualquier momento, salvo que
            exista un deber legal o contractual que requiera mantener el tratamiento de
            los datos. La revocación equivale, para todos los efectos prácticos, a
            solicitar la eliminación de la cuenta TAXOR.
          </p>
        </Section>

        <Section
          title="Constancia de aceptación"
          variant="brand"
          icon={<CheckCircle2 className="h-6 w-6" />}
        >
          <p>
            Al registrarse en la plataforma TAXOR y marcar la casilla correspondiente, el
            Titular manifiesta haber leído, comprendido y aceptado íntegramente esta
            Autorización para el Tratamiento de Datos Personales.
          </p>
          <p>
            Esta autorización se entiende otorgada en la fecha y hora del registro,
            quedando constancia electrónica auditable (IP, User-Agent, versión exacta del
            documento) en los registros de TAXOR conforme a la Ley 1581 de 2012.
          </p>
          <p className="text-center text-xs text-fg-muted">
            Otorgada en cumplimiento de la Ley Estatutaria 1581 de 2012, el Decreto 1377
            de 2013, y demás normas concordantes de la República de Colombia.
          </p>
        </Section>

        <RelatedDocs except="habeas-data" />
      </div>
    </Shell>
  )
}

// ════════════════════════════════════════════════════════════════════
// Página 4 — POLÍTICA DE COOKIES
// ════════════════════════════════════════════════════════════════════

export function CookiesPage() {
  return (
    <Shell>
      <PageHeader
        icon={<Cookie className="h-6 w-6" />}
        title="Política de Cookies"
        subtitle="Qué cookies usamos y por qué"
      />
      <div className="space-y-6">
        <Section title="Resumen" variant="brand" icon={<Sparkles className="h-5 w-5" />}>
          <p>
            TAXOR usa la cantidad <strong>mínima</strong> de cookies necesarias para que
            el servicio funcione. <strong>No usamos tracking de terceros</strong>, ni
            Facebook Pixel, ni Google Analytics, ni ningún otro analítico publicitario.
          </p>
        </Section>

        <Section
          number="1"
          title="Cookies estrictamente necesarias"
          icon={<Lock className="h-5 w-5" />}
        >
          <ul>
            <li>
              <strong>taxor_refresh</strong> — token de refresco JWT (HttpOnly, Secure,
              SameSite=None, scope{' '}
              <code className="rounded bg-bg-elevated px-1.5 py-0.5 text-xs">
                /api/v1/auth/refresh
              </code>
              ).
              <br />
              Permite mantener tu sesión sin re-loguear cada 15 minutos. Sin ella el
              servicio no funciona correctamente, así que no requiere consentimiento
              específico (excepción del Decreto 1377/2013 art. 10 — datos necesarios para
              la prestación del servicio contratado).
            </li>
          </ul>
        </Section>

        <Section number="2" title="Cookies analíticas / publicitarias">
          <p>
            Hoy: <strong>cero</strong>. Si en el futuro agregamos analítica anónima o
            herramientas de marketing, te pediremos <strong>consentimiento explícito</strong>{' '}
            antes — con un banner de cookies y opción granular por categoría.
          </p>
        </Section>

        <Section number="3" title="localStorage / sessionStorage">
          <p>Usamos <code>localStorage</code> únicamente para preferencias de UI no sensibles:</p>
          <ul>
            <li><strong>taxor-theme</strong> — preferencia de tema (claro/oscuro/automático).</li>
          </ul>
          <p>
            <strong>No almacenamos tokens de autenticación en localStorage ni en
            sessionStorage.</strong> Los tokens viven en memoria del SPA y se rotan vía
            cookie HttpOnly — esto cierra la superficie de exfiltración por XSS.
          </p>
        </Section>

        <Section number="4" title="Cómo controlar las cookies">
          <p>
            La cookie <code>taxor_refresh</code> se elimina automáticamente al cerrar
            sesión desde el menú de usuario, o cuando expira (14 días sin uso). También
            puedes limpiar las cookies desde la configuración de tu navegador, pero esto
            te obligará a re-loguear.
          </p>
        </Section>

        <RelatedDocs except="cookies" />
      </div>
    </Shell>
  )
}
